import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Truck, 
  Save, 
  TestTube, 
  CheckCircle, 
  XCircle,
  Loader2,
  Settings,
  MapPin,
  Package,
  FileText,
  AlertCircle,
  RefreshCw,
  Plus,
  Trash2,
  Edit2,
  IndianRupee,
  Star,
  MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DelhiveryIntegrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    api_key: '',
    client_name: '',
    test_mode: true,
    label_format: 'a4',
    default_warehouse_id: '',
    cod_enabled: true,
    auto_manifest: true,
    serviceability_mode: 'api',
    custom_printable_text: ''  // Custom text to print on shipping labels
  });

  useEffect(() => {
    fetchIntegrations();
    fetchWarehouses();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/couriers/integrations/delhivery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/enterprise/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarehouses(response.data.locations || response.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      api_key: '',
      client_name: '',
      test_mode: true,
      label_format: 'a4',
      default_warehouse_id: '',
      cod_enabled: true,
      auto_manifest: true,
      serviceability_mode: 'api'
    });
    setTestResult(null);
  };

  const openAddDialog = () => {
    resetForm();
    setEditingIntegration(null);
    setDialogOpen(true);
  };

  const openEditDialog = (integration) => {
    setEditingIntegration(integration);
    setFormData({
      name: integration.name || '',
      api_key: integration.credentials?.api_key || '', // Show existing API key
      client_name: integration.credentials?.client_name || '',
      test_mode: integration.test_mode || false,
      label_format: integration.settings?.label_format || 'a4',
      default_warehouse_id: integration.settings?.default_warehouse_id || '',
      cod_enabled: integration.settings?.cod_enabled !== false,
      auto_manifest: integration.settings?.auto_manifest !== false,
      serviceability_mode: integration.settings?.serviceability_mode || 'api',
      custom_printable_text: integration.settings?.custom_printable_text || ''
    });
    setTestResult(null);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter an integration name');
      return;
    }

    if (!editingIntegration && !formData.api_key.trim()) {
      toast.error('Please enter API key');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      const payload = {
        courier_type: 'delhivery',
        name: formData.name.trim(),
        credentials: {
          client_name: formData.client_name
        },
        settings: {
          label_format: formData.label_format,
          default_warehouse_id: formData.default_warehouse_id,
          cod_enabled: formData.cod_enabled,
          auto_manifest: formData.auto_manifest,
          serviceability_mode: formData.serviceability_mode,
          custom_printable_text: formData.custom_printable_text?.trim() || null
        },
        test_mode: formData.test_mode,
        is_active: true
      };

      // Only include API key if provided (for updates, user might leave it empty)
      if (formData.api_key.trim()) {
        payload.credentials.api_key = formData.api_key.trim();
      }

      // If editing, include the ID
      if (editingIntegration) {
        payload.id = editingIntegration.id;
      }

      await axios.post(
        `${API_BASE_URL}/couriers/integrations`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(editingIntegration ? 'Integration updated!' : 'Integration added!');
      setDialogOpen(false);
      resetForm();
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (integration) => {
    if (!window.confirm(`Are you sure you want to delete "${integration.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/couriers/integrations/${integration.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Integration deleted');
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete integration');
    }
  };

  const handleSetDefault = async (integration) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/couriers/integrations/${integration.id}/set-default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`"${integration.name}" is now the default`);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/couriers/integrations/delhivery/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTestResult(response.data);
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.response?.data?.detail || 'Connection test failed' 
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/integrations')}
          className="border-wt-border"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-gradient-to-br from-red-500 to-red-600">
            <Truck className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Delhivery Integration</h1>
            <p className="text-wt-text-muted">
              {integrations.length} account{integrations.length !== 1 ? 's' : ''} configured
            </p>
          </div>
        </div>
      </div>

      {/* Add New Account Button */}
      <div className="flex justify-end">
        <Button
          onClick={openAddDialog}
          className="bg-wt-gold hover:bg-wt-gold-dark text-black"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Delhivery Account
        </Button>
      </div>

      {/* Integrations List */}
      {integrations.length === 0 ? (
        <div className="bg-wt-surface border border-wt-border rounded-xl p-12 text-center">
          <Truck className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-50" />
          <h3 className="text-xl font-semibold text-wt-text-primary mb-2">No Delhivery Accounts</h3>
          <p className="text-wt-text-muted mb-4">
            Add your first Delhivery account to start shipping orders
          </p>
          <Button
            onClick={openAddDialog}
            className="bg-wt-gold hover:bg-wt-gold-dark text-black"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Account
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {integrations.map((integration, idx) => (
            <motion.div
              key={integration.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-5 hover:border-wt-gold/30 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-red-500/10">
                    <Truck className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-wt-text-primary">
                        {integration.name}
                      </h3>
                      {integration.is_default && (
                        <Badge className="bg-wt-gold/20 text-wt-gold border-wt-gold/30">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Default
                        </Badge>
                      )}
                      {integration.test_mode && (
                        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                          Test Mode
                        </Badge>
                      )}
                      {integration.is_active ? (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                          Inactive
                        </Badge>
                      )}
                    </div>
                    
                    <div className="text-sm text-wt-text-muted mb-2">
                      API Key: <span className="font-mono text-xs">{integration.credentials?.api_key || 'Not set'}</span>
                      {integration.credentials?.client_name && (
                        <span className="ml-3">Client: {integration.credentials.client_name}</span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-3 text-xs text-wt-text-muted">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        Label: {integration.settings?.label_format?.toUpperCase() || 'A4'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Package className="h-3 w-3" />
                        COD: {integration.settings?.cod_enabled !== false ? 'Yes' : 'No'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Settings className="h-3 w-3" />
                        {integration.settings?.serviceability_mode === 'api' ? 'API Check' : 'Manual'}
                      </span>
                    </div>
                  </div>
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-wt-text-muted">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-wt-surface border-wt-border">
                    <DropdownMenuItem 
                      onClick={() => openEditDialog(integration)}
                      className="text-wt-text-primary cursor-pointer"
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => navigate(`/integrations/shipping-rate-cards?open=${integration.id}`)}
                      className="text-wt-text-primary cursor-pointer"
                      data-testid={`delhivery-rate-card-${integration.id}`}
                    >
                      <IndianRupee className="h-4 w-4 mr-2" />
                      Rate Card
                    </DropdownMenuItem>
                    {!integration.is_default && (
                      <DropdownMenuItem 
                        onClick={() => handleSetDefault(integration)}
                        className="text-wt-gold cursor-pointer"
                      >
                        <Star className="h-4 w-4 mr-2" />
                        Set as Default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => handleDelete(integration)}
                      className="text-red-400 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Features Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-wt-paper border border-wt-border rounded-xl p-6"
      >
        <h3 className="text-sm font-medium text-wt-text-muted mb-3">FEATURES</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="flex items-center gap-2 text-sm text-wt-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-400" />
            Automatic AWB Generation
          </div>
          <div className="flex items-center gap-2 text-sm text-wt-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-400" />
            Real-time Tracking Updates
          </div>
          <div className="flex items-center gap-2 text-sm text-wt-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-400" />
            NDR Management
          </div>
          <div className="flex items-center gap-2 text-sm text-wt-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-400" />
            COD Support
          </div>
          <div className="flex items-center gap-2 text-sm text-wt-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-400" />
            Multiple Accounts Support
          </div>
          <div className="flex items-center gap-2 text-sm text-wt-text-secondary">
            <CheckCircle className="h-4 w-4 text-green-400" />
            Shipping Labels
          </div>
        </div>
      </motion.div>

      {/* Quick Links */}
      {integrations.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-6"
        >
          <h3 className="text-sm font-medium text-wt-text-muted mb-3">QUICK LINKS</h3>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/integrations/courier-settings')}
              className="border-wt-border text-wt-text-primary hover:bg-wt-subtle"
            >
              <Settings className="h-4 w-4 mr-2" />
              Serviceability & Auto-Assign Rules
            </Button>
          </div>
        </motion.div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Truck className="h-5 w-5 text-red-400" />
              {editingIntegration ? 'Edit Delhivery Account' : 'Add Delhivery Account'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Integration Name */}
            <div>
              <Label className="text-wt-text-primary">Account Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Delhivery Small, Delhivery Premium"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                Give a unique name to identify this account
              </p>
            </div>

            {/* API Credentials */}
            <div className="bg-wt-surface border border-wt-border rounded-lg p-4 space-y-4">
              <h4 className="text-sm font-medium text-wt-text-primary">API Credentials</h4>
              
              <div>
                <Label className="text-wt-text-primary">
                  API Token {!editingIntegration && '*'}
                </Label>
                <Input
                  type="text"
                  value={formData.api_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder={editingIntegration ? 'Enter new API token or leave empty to keep existing' : 'Enter your Delhivery API token'}
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary font-mono text-sm"
                />
                {editingIntegration && (
                  <p className="text-xs text-wt-text-muted mt-1">
                    Leave empty to keep existing API key
                  </p>
                )}
              </div>

              <div>
                <Label className="text-wt-text-primary">Client Name (Optional)</Label>
                <Input
                  value={formData.client_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                  placeholder="Your Delhivery client name"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Test Mode</Label>
                  <p className="text-xs text-wt-text-muted">Use staging environment for testing</p>
                </div>
                <Switch
                  checked={formData.test_mode}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, test_mode: v }))}
                />
              </div>
            </div>

            {/* Shipping Settings */}
            <div className="bg-wt-surface border border-wt-border rounded-lg p-4 space-y-4">
              <h4 className="text-sm font-medium text-wt-text-primary">Shipping Settings</h4>
              
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-wt-text-primary">Default Pickup Location</Label>
                  <Select 
                    value={formData.default_warehouse_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, default_warehouse_id: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue placeholder="Select warehouse" />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      {warehouses.map((wh) => (
                        <SelectItem key={wh.id} value={wh.id}>
                          {wh.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-wt-text-primary">Label Format</Label>
                  <Select 
                    value={formData.label_format} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, label_format: v }))}
                  >
                    <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-wt-surface border-wt-border">
                      <SelectItem value="a4">A4 Standard</SelectItem>
                      <SelectItem value="4x6">4x6 Thermal</SelectItem>
                      <SelectItem value="4x4">4x4 Compact</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-wt-text-primary">Serviceability Check</Label>
                <Select 
                  value={formData.serviceability_mode} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, serviceability_mode: v }))}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    <SelectItem value="api">API Check (Real-time)</SelectItem>
                    <SelectItem value="manual">Manual Pincode List</SelectItem>
                    <SelectItem value="both">Both (API + Manual)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">COD Enabled</Label>
                  <p className="text-xs text-wt-text-muted">Allow Cash on Delivery shipments</p>
                </div>
                <Switch
                  checked={formData.cod_enabled}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, cod_enabled: v }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-wt-text-primary">Auto Manifest</Label>
                  <p className="text-xs text-wt-text-muted">Automatically manifest after booking</p>
                </div>
                <Switch
                  checked={formData.auto_manifest}
                  onCheckedChange={(v) => setFormData(prev => ({ ...prev, auto_manifest: v }))}
                />
              </div>
              
              {/* Custom Printable Text */}
              <div className="pt-2 border-t border-wt-border">
                <Label className="text-wt-text-primary">Custom Printable Text</Label>
                <p className="text-xs text-wt-text-muted mb-2">
                  Add custom text to be printed on shipping labels (e.g., special instructions, promo message)
                </p>
                <textarea
                  value={formData.custom_printable_text}
                  onChange={(e) => setFormData(prev => ({ ...prev, custom_printable_text: e.target.value }))}
                  placeholder="e.g., Thank you for shopping with us! For returns, contact support@company.com"
                  className="w-full h-20 px-3 py-2 text-sm bg-wt-bg border border-wt-border rounded-lg text-wt-text-primary placeholder:text-wt-text-muted resize-none focus:outline-none focus:ring-2 focus:ring-wt-gold/50"
                  maxLength={150}
                />
                <p className="text-xs text-wt-text-muted mt-1">{formData.custom_printable_text?.length || 0}/150 characters</p>
              </div>
            </div>

            {/* Test Connection Result */}
            {testResult && (
              <div className={`p-4 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-400" />
                  )}
                  <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                    {testResult.message}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || saving}
              className="border-wt-border text-wt-text-primary"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <TestTube className="h-4 w-4 mr-2" />
              )}
              Test Connection
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-wt-gold hover:bg-wt-gold-dark text-black"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingIntegration ? 'Update' : 'Save'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DelhiveryIntegrationPage;
